import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { formatDate, formatMoney } from "@/lib/format";
import { computePorijeklo, pct, downloadExport } from "@/lib/porijeklo";
import { Plus, FileDown, FileSpreadsheet, Pencil, Trash2 } from "lucide-react";
import PorijekloEditor from "@/components/porijeklo/PorijekloEditor";

export default function Porijeklo() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/porijeklo").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Obrisati ovu izjavu?")) return;
    await api.delete(`/porijeklo/${id}`);
    toast.success("Izjava obrisana");
    load();
  };

  if (editing) {
    return <PorijekloEditor initial={editing} onClose={() => { setEditing(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="porijeklo-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold font-heading tracking-tight">Izjava o porijeklu</h1>
          <p className="text-sm text-muted-foreground mt-1">Uploadujte izdatnicu materijala (PDF) — crveno označeni redni brojevi idu u materijal sa porijeklom.</p>
        </div>
        <Button onClick={() => setEditing({})} data-testid="porijeklo-new-btn"><Plus className="h-4 w-4 mr-1.5" /> Nova izjava</Button>
      </div>
      <div className="rounded-xl border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="porijeklo-list">
          <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="p-3 text-left">Kupac</th>
              <th className="p-3 text-left">Proizvod</th>
              <th className="p-3 text-left">Izdatnica</th>
              <th className="p-3 text-right">FCA EUR</th>
              <th className="p-3 text-right">Bez porijekla / FCA</th>
              <th className="p-3 text-right">Akcije</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Još nema izjava. Kliknite "Nova izjava".</td></tr>
            )}
            {list.map((d) => (
              <tr key={d.id} className="border-t" data-testid={`porijeklo-row-${d.id}`}>
                <td className="p-3">
                  <div className="font-semibold">{d.customer_name || "—"}</div>
                  {d.note && <div className="text-xs text-muted-foreground" data-testid={`porijeklo-note-${d.id}`}>{d.note}</div>}
                </td>
                <td className="p-3">{d.product || "—"}</td>
                <td className="p-3 whitespace-nowrap">{d.doc_number || "—"}{d.doc_date ? ` · ${formatDate(d.doc_date)}` : ""}</td>
                <td className="p-3 text-right tabular-nums">{formatMoney(Number(d.fca_eur) || 0, "EUR")}</td>
                <td className="p-3 text-right tabular-nums font-semibold">{pct(computePorijeklo(d).ratio)}</td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="outline" onClick={() => downloadExport(d.id, "pdf")} data-testid={`porijeklo-pdf-${d.id}`}><FileDown className="h-4 w-4 mr-1" />PDF</Button>
                    <Button size="sm" variant="outline" onClick={() => downloadExport(d.id, "xlsx")} data-testid={`porijeklo-xlsx-${d.id}`}><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
                    <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => setEditing(d)} data-testid={`porijeklo-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive" onClick={() => remove(d.id)} data-testid={`porijeklo-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
